import React from 'react';

import { getCurrencySymbol } from '../../helpers/currency-symbol';
import { IkeaProductCard } from '../../helpers/ikea-product-feed/interfaces';
import { isBrowser } from '../../helpers/is-browser';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';

interface TenantIkeaProductCardProps {
  card: IkeaProductCard;
  cardIndex: number;
  buyNowText?: string;
  showCarousel?: boolean;
  cardHeader: string;
  pageName: string;
  user?: any;
}

const productPriority: string[] = [
  'breathtakingitem',
  'isLastChance',
  'timeRestrictedOffer',
  'isTopSeller',
  'isNew',
  'isLimitEdition',
  'newLowerPrice',
];

const sendEventToDataLayer = (
  categories: string[] | undefined,
  productMktAttribute: string,
  pageName?: string,
  user?: any,
  isLivliFamily?: boolean,
) => {
  if (isBrowser && dataLayer) {
    dataLayer.push({
      event: 'ikea-product-cta',
      product_category: categories || [],
      product_mkt_attribute: productMktAttribute,
      page_title: pageName,
      user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
    });
  }
};

export const TenantIkeaProductCard = ({
  card,
  cardIndex,
  buyNowText,
  showCarousel,
  cardHeader,
  pageName,
  user,
}: TenantIkeaProductCardProps) => {
  const isLivliFamily = useIsMPLivliFamily();
  const getTag = (card: IkeaProductCard): string => {
    let tag = '';
    productPriority.some((prop) => {
      const propValue = card[prop as keyof IkeaProductCard];
      if (propValue !== undefined && propValue !== null && propValue !== '') {
        tag = propValue;
        return true; // Break the loop if a prop is found
      }
      return false;
    });
    return tag;
  };

  // const handlePDPLink = (url: string) => {
  //   let utm_source = encodeURIComponent('dynamic'); // replace with meeting place
  //   let utm_medium = encodeURIComponent('referral');
  //   let utm_campaign = encodeURIComponent('ingkacenter');
  //   // return `${url}?utm_source=${utm_source}&utm_medium=${utm_medium}&utm_campaign=${utm_campaign}`;
  //   return url;
  // };

  const extractNumericAndText = (price: string) => {
    const matches = price.match(/([0-9.]+)(.*)/);

    if (matches) {
      const numericPart = matches[1];
      const textPart = matches[2];
      return { numericPart: formatNumber(numericPart), textPart };
    }
    // If no alphabetical characters are found, consider the whole string as numeric
    const numericOnlyRegex = /^[0-9.]+$/;
    if (numericOnlyRegex.test(price)) {
      return { numericPart: formatNumber(price), textPart: '' };
    }

    return { numericPart: '', textPart: '' };
  };

  function formatNumber(price: string) {
    // Remove trailing ".00" if present
    const numWithoutDecimal = price
      .replace(/(\.\d*?)0*$/, '$1')
      .replace(/\.$/, '');

    const parts = numWithoutDecimal.split('.');
    const formattedInteger = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (parts.length > 1) {
      return formattedInteger + '.' + parts[1];
    } else {
      return formattedInteger;
    }
  }

  const getCurrency = (card: IkeaProductCard) => {
    const price = card?.sale_price || card?.price || '';
    const { textPart } = extractNumericAndText(price);

    return textPart ? getCurrencySymbol(textPart.trim()) : '';
  };

  const getPrice = (card: IkeaProductCard) => {
    const { sale_price = '', price = '' } = card;
    const salePriceData = extractNumericAndText(sale_price);
    const priceData = extractNumericAndText(price);
    return (
      <div className="flex flex-col">
        <p className="text-bodys text-text-default line-through decoration-text-light decoration-2 h-5">
          {salePriceData?.numericPart ? priceData?.numericPart : ''}
        </p>
        <p className="relative text-headingl text-text-default">
          <span className="text-bodys text-text-default">
            {getCurrency(card)}
          </span>
          {salePriceData?.numericPart
            ? salePriceData?.numericPart
            : priceData?.numericPart}
        </p>
      </div>
    );
  };

  const handleCardClickAndSendEvent = (
    card: IkeaProductCard,
    pageName: string,
  ) => {
    const categories = card?.google_product_category
      ?.split('>')
      ?.map((category) => category.trim());
    sendEventToDataLayer(
      categories,
      getTag(card),
      pageName,
      user,
      isLivliFamily,
    );
  };

  const buyNowButtonClickHandler = (
    event: React.MouseEvent<HTMLElement>,
    card: IkeaProductCard,
  ) => {
    event.preventDefault();
    if (isBrowser) {
      window.open(card?.link, '_blank');
    }
  };

  const getCategories = (card: IkeaProductCard) => {
    const categories = card?.google_product_category
      ?.split('>')
      ?.map((category) => category.trim())
      ?.toString()
      ?.replaceAll(',', ', ');

    return categories;
  };

  return (
    <a
      href={card?.link}
      target="_blank"
      key={card?.id}
      className={
        'ikea flex cursor-pointer flex-col rounded-xl bg-background-primary-light p-5 lg:hover:bg-background-secondary focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light'
      }
      onMouseUp={() => handleCardClickAndSendEvent(card, pageName)}
      rel="noreferrer"
    >
      <div className="w-full flex">
        <div className="w-[50%] pr-[5%]">
          {card?.image_link ? (
            <div className="h-[100%] ">
              <img
                src={card?.image_link + '?fm=webp&q=50'}
                alt={card.title || 'alt text not found'}
                className="aspect-square transform-gpu rounded-lg"
              />
            </div>
          ) : (
            <div className="aspect-1.5/1 flex-grow"></div>
          )}
        </div>
        <div className="w-[50%] px-1">
          <div className="mb-2 mt-4 text-headingm text-neutral-grey-700 font-title">
            {cardHeader}
          </div>
          <div className="mt-2 font-title text-buttonlabelm text-text-sticker-blue">
            {getTag(card)}
          </div>
          <div className="mt-2 flex h-10 w-full items-center">
            <p className="line-clamp-2 max-h-full w-full overflow-hidden text-ellipsis font-sans text-bodymheavy">
              {card?.title?.toUpperCase()}
            </p>
          </div>
          <div className="mb-2 mt-1 line-clamp-1 h-[24px] w-full overflow-hidden text-ellipsis break-all text-bodym">
            {getCategories(card)}
          </div>
          <div className="mt-auto">
            {getPrice(card)}
            <button
              className="mt-[10px] inline-block px-5 rounded-full bg-accent-primary-normal py-[11px] text-buttonlabelm leading-[18px] text-brand-grey-4 lg:hover:bg-accent-primary-dark focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light active:bg-brand-yellow-3"
              onClick={(event) => buyNowButtonClickHandler(event, card)}
            >
              {buyNowText}
            </button>
          </div>
          {showCarousel && <div className="h-16 xl:h-8"></div>}
        </div>
      </div>
    </a>
  );
};
